// Story 10.2 — Hook que escucha eventos del WS para mantener deltas en
// vivo de un Bot Card. La lógica del reducer vive en `realtime-reducer.ts`
// (deps-free, testable).
import { useEffect, useReducer } from 'react';
import {
  INITIAL_REALTIME_STATE,
  type RealtimeMetricsState,
  realtimeReducer,
} from '@/hooks/realtime-reducer';
import { useWsEvent } from '@/hooks/use-ws-event';

const ERROR_PULSE_MS = 3000;

interface WsPayloadMessage {
  botId?: string | number;
  from?: string;
}

/**
 * P-233: matchea el `event.botId` del envelope contra el bot row.
 * El orchestrator emite `message:*` con `String(botRow.id)` (numeric),
 * pero el flow-engine emite `flow:*` con `bot.bot.name` (DSL slug). Sin
 * aceptar ambos, `flow:error` nunca matchearía y la card no se pintaría
 * en rojo. Defensive match contra ambos identificadores.
 */
function payloadBotIdMatches(
  envelope: { botId?: string },
  botId: number,
  botName: string,
): boolean {
  if (envelope.botId == null) return false;
  return envelope.botId === String(botId) || envelope.botId === botName;
}

interface UseBotRealtimeMetricsOptions {
  /** P-231: identificador del último snapshot del backend. Cuando cambia,
   *  los deltas se resetean para que (snapshot + delta) no double-count. */
  resetSignal?: number | string;
  /** IG-20: callback opcional que dispara el toast cuando entra `flow:error`. */
  onFlowError?: () => void;
}

export function useBotRealtimeMetrics(
  botId: number,
  botName: string,
  options: UseBotRealtimeMetricsOptions = {},
): RealtimeMetricsState {
  const [state, dispatch] = useReducer(realtimeReducer, INITIAL_REALTIME_STATE);
  const { resetSignal, onFlowError } = options;

  useWsEvent('message:received', (envelope) => {
    if (!payloadBotIdMatches(envelope, botId, botName)) return;
    const from = (envelope.payload as WsPayloadMessage | undefined)?.from ?? 'unknown';
    dispatch({ type: 'message-received', from });
  });

  useWsEvent('message:sent', (envelope) => {
    if (!payloadBotIdMatches(envelope, botId, botName)) return;
    dispatch({ type: 'message-sent' });
  });

  useWsEvent('bot:started', (envelope) => {
    if (!payloadBotIdMatches(envelope, botId, botName)) return;
    dispatch({ type: 'bot-started' });
  });

  useWsEvent('bot:stopped', (envelope) => {
    if (!payloadBotIdMatches(envelope, botId, botName)) return;
    dispatch({ type: 'bot-stopped' });
  });

  useWsEvent('flow:error', (envelope) => {
    if (!payloadBotIdMatches(envelope, botId, botName)) return;
    dispatch({ type: 'flow-error' });
    onFlowError?.();
  });

  // P-231: reset de los deltas cuando el snapshot del backend se refresca,
  // para que (stats.data + realtime.X) no cuente dos veces los eventos
  // que ya están en el snapshot recién obtenido.
  useEffect(() => {
    if (resetSignal == null) return;
    dispatch({ type: 'reset' });
  }, [resetSignal]);

  // Auto-clear del errorPulse tras N ms.
  useEffect(() => {
    if (!state.errorPulse) return;
    const timer = window.setTimeout(() => {
      dispatch({ type: 'error-pulse-clear' });
    }, ERROR_PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [state.errorPulse]);

  return state;
}
